import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, CartAddEvent, CartUpdateEvent, QuantitySelectorUpdateEvent } from '@theme/events';

class CartItemsComponent extends Component {
  /** @type {Map<number, AbortController>} */
  #abortControllers = new Map();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * @param {Event} event
   */
  #onQuantityChange = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const line = event.detail.cartLine;
    if (!line) return;

    this.#updateLine(line, event.detail.quantity);
  };

  /**
   * @param {MouseEvent} event
   */
  removeLine = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const line = Number(event.target.dataset.cartLine);
    if (!line) return;

    this.#updateLine(line, 0);
  };

  /**
   * @param {number} line
   * @param {number} quantity
   */
  #updateLine(line, quantity) {
    this.#abortControllers.get(line)?.abort();

    const abortController = new AbortController();
    this.#abortControllers.set(line, abortController);

    // Animate removal before fetching
    const removePromise = quantity === 0
      ? this.#animateRemoval(line)
      : Promise.resolve();

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.sectionId,
      sections_url: window.location.pathname,
    });

    Promise.all([
      removePromise,
      fetch(Theme.routes.cart_change_url, { ...fetchConfig('json', { body }), signal: abortController.signal }),
    ])
      .then(async ([, res]) => {
        const response = await res.json();

        if (!res.ok) {
          await sectionRenderer.renderSection(this.sectionId, { cache: false });
          this.#showLineError(line, response.description || response.message);
          return;
        }

        this.#updateCartCount(response.item_count);

        this.dispatchEvent(
          new CartUpdateEvent(response, this.sectionId, {
            source: 'cart-items-component',
            sections: response.sections,
            itemCount: response.item_count,
          })
        );

        const html = response.sections?.[this.sectionId];
        if (html) {
          morphSection(this.sectionId, html, this.isDrawer ? 'hydration' : 'full');
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false });
        }

        this.#restoreScrollLock();

        if (this.isDrawer && quantity === 0) {
          requestAnimationFrame(() => {
            const title = this.closest('dialog')?.querySelector('.dialog-title');
            if (title instanceof HTMLElement) {
              title.setAttribute('tabindex', '-1');
              title.focus();
            }
          });
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error(error);
      })
      .finally(() => {
        if (this.#abortControllers.get(line) === abortController) {
          this.#abortControllers.delete(line);
        }
      });
  }

  /**
   * @param {number} line - 1-based line index
   * @param {string} message
   */
  #showLineError(line, message) {
    const items = this.querySelectorAll('.cart-item');
    const item = items[line - 1];
    if (!(item instanceof HTMLElement)) return;

    // Remove any existing error on this item
    item.querySelector('.cart-item__error')?.remove();

    const error = document.createElement('p');
    error.className = 'cart-item__error';
    error.setAttribute('role', 'alert');
    error.textContent = message;

    // Start collapsed
    error.style.maxHeight = '0';
    error.style.opacity = '0';
    item.appendChild(error);

    // Expand to natural height (includes padding-top)
    const naturalHeight = error.scrollHeight;
    // eslint-disable-next-line no-unused-expressions
    error.offsetHeight;
    error.style.maxHeight = `${naturalHeight}px`;
    error.style.opacity = '1';

    // Auto-dismiss after 5s
    setTimeout(() => {
      error.classList.add('cart-item__error--hiding');
      error.addEventListener('transitionend', (e) => {
        if (e.target === error && e.propertyName === 'max-height') error.remove();
      });
    }, 5000);
  }

  /**
   * @param {number} line - 1-based line index
   * @returns {Promise<void>}
   */
  #animateRemoval(line) {
    const items = this.querySelectorAll('.cart-item');
    const item = items[line - 1];
    if (!(item instanceof HTMLElement)) return Promise.resolve();

    // Lock the current height so CSS can transition from it to 0
    item.style.maxHeight = `${item.offsetHeight}px`;

    // Force reflow then add class to trigger all transitions
    // eslint-disable-next-line no-unused-expressions
    item.offsetHeight;
    item.classList.add('cart-item--removing');

    return new Promise((resolve) => {
      item.addEventListener('transitionend', (e) => {
        if (e.target === item && e.propertyName === 'max-height') resolve();
      });
    });
  }

  /**
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    if (!(event instanceof CartUpdateEvent) && !(event instanceof CartAddEvent)) return;
    if (event.target === this) return;

    this.#updateCartCount(event.detail?.data?.itemCount);

    // Skip morph for CartAddEvent in drawer — cart-drawer-component handles it with animation
    if (event instanceof CartAddEvent && this.isDrawer) return;

    const html = event.detail?.data?.sections?.[this.sectionId];
    if (html) {
      morphSection(this.sectionId, html, this.isDrawer ? 'hydration' : 'full');
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    this.#restoreScrollLock();
  };

  /**
   * @param {number | undefined} itemCount
   */
  #updateCartCount(itemCount) {
    if (!Number.isFinite(itemCount)) return;

    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = String(itemCount);
    });
  }

  #restoreScrollLock() {
    const dialog = this.closest('dialog');
    if (dialog?.open) {
      dialog.setAttribute('scroll-lock', '');
    }
  }

  get sectionId() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      throw new Error('Section id missing on cart-items-component');
    }

    return sectionId;
  }

  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
